import { Component, ElementRef, inject, OnDestroy, OnInit, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';
import { Router } from '@angular/router';
import { Subject, switchMap, takeUntil } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

// Components
import { PreviewMessageComponent } from '../preview-message/preview-message.component';
import { ConfirmSubmitComponent } from '../confirm-submit/confirm-submit.component';

// Services
import { AddMessageNotificationService } from '../../services/add-message/add-message-notification.service';
import { ResponsiveBreakpointService } from '../../services/breakpoint/responsive-breakpoint.service';
import { SelectedMessageDetailService } from '../../services/selected-message-detail/selected-message-detail.service';
import { StatementMessagingHttpClientService } from '../../services/http-bridge/statement-messaging-http-client.service';
import { MessageDataService } from '../../services/message-details/message-details.service';
import { FundDataService } from '../../services/fund-id/fund-id.service';
import { OktaService } from '../../services/okta/okta.service';

// Models
import {
  AddUpdateDeleteMessageModel,
  AddUpdateDeleteResponseModel,
  StatementMessagingMessageModel,
} from '../../core/interfaces/statement-messaging-run.model';

// Guards
import { ComponentWithUnsavedChanges } from '../../core/guards/add-message-unsaved-form.guard';

// Utilities
import {
  getMessageCodeByKey,
  getMessageCodeByValue,
  getMessageTypeValues,
  getRemainingSpace,
  getTextValidationErrors,
  MessageTypeCode,
  TextWrappingResult,
  validateTextForMessageType,
  wrapTextForMessageType,
  getMessageLimits,
} from '../../core/utility/message-enum.util';
import { getFundCodeByLabel, getFundCodeLabels } from '../../core/utility/fund-code-enum.util';
import { FundIdWithName } from '../../core/interfaces/fund.model';

@Component({
  selector: 'app-message-add',
  standalone: false,
  templateUrl: './message-add.component.html',
  styleUrl: './message-add.component.scss',
})
export class MessageAddComponent implements OnInit, OnDestroy, ComponentWithUnsavedChanges {
  // ============================================
  // Constants
  // ============================================
  private readonly PADDING_MESSAGE_ID = 10;
  private readonly ACTION_CODE_ADD = 'A';

  // ============================================
  // View Children
  // ============================================
  @ViewChild('confirmationModal') confirmationModal!: ConfirmSubmitComponent;
  @ViewChild('submitConfirmationModal') submitConfirmationModal!: ConfirmSubmitComponent;
  @ViewChild('messagePreviewModal') messagePreviewModal!: PreviewMessageComponent;

  @ViewChildren('messageLineInput', { read: ElementRef }) messageLineInputs!: QueryList<ElementRef>;

  // ============================================
  // Public Properties
  // ============================================
  showErrors = false;
  isWaiting = false;
  bypassSubmit = false;

  readonly messageTypes = getMessageTypeValues();
  readonly fundCodes: string[] = getFundCodeLabels();

  // Fund IDs from service
  fundIds: FundIdWithName[] = [];
  isFundIdsLoading = false;
  fundIdsError: string | null = null;

  // Text Wrapping Properties
  textWrappingResult: TextWrappingResult | null = null;
  wrappedMessageLines: string[] = [];
  textValidationErrors: string[] = [];
  remainingSpace: {
    remainingLines: number;
    remainingCharsOnCurrentLine: number;
    canAddMoreText: boolean;
  } | null = null;

  messageLines: FormControl[] = [];
  maxLinesForCurrentType = 0;
  maxCharsPerLine = 0;

  // ============================================
  // Form Group
  // ============================================
  formGroup: FormGroup = new FormGroup({
    message_number: new FormControl('', [(control) => Validators.required(control)]),
    message_type: new FormControl('', [(control) => Validators.required(control)]),
    fund_id: new FormControl(''),
    fund_code: new FormControl(''),
    header: new FormControl(''),
    isPermanent: new FormControl(false),
  });

  // ============================================
  // Services
  // ============================================
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly httpClient = inject(StatementMessagingHttpClientService);
  private readonly notificationService = inject(AddMessageNotificationService);
  private readonly selectedMessageService = inject(SelectedMessageDetailService);
  private readonly messageDataService = inject(MessageDataService);
  private readonly fundDataService = inject(FundDataService);
  private readonly oktaService = inject(OktaService);
  protected readonly responsiveService = inject(ResponsiveBreakpointService);

  // ============================================
  // Private Properties
  // ============================================
  private readonly destroy$ = new Subject<void>();

  // ============================================
  // Lifecycle Hooks
  // ============================================

  ngOnInit(): void {
    this.setupMessageTypeWatcher();
    //this.setupTextValidation();
    this.subscribeFundIds();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ============================================
  // Fund IDs Subscription
  // ============================================

  /**
   * Subscribe to fund IDs from service
   */
  private subscribeFundIds(): void {
    // Subscribe to fund IDs data
    this.fundDataService.fundIds$
      .pipe(takeUntil(this.destroy$))
      .subscribe((fundIds) => {
        //0000 fundPortId should not be visible it stands for ''(empty)
        this.fundIds = fundIds.filter(fundId => fundId.fundPortId !== '0000');
      });

    // Subscribe to loading state
    this.fundDataService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.isFundIdsLoading = loading;
      });

    // Subscribe to error state
    this.fundDataService.error$
      .pipe(takeUntil(this.destroy$))
      .subscribe((error) => {
        this.fundIdsError = error;
        if (error) {
          this.notificationService.showError(
            'Failed to load fund IDs. Please refresh the page.',
            'Fund Data Error'
          );
        }
      });
  }

  /**
   * Manually reload fund IDs (in case of error)
   */
  reloadFundIds(): void {
    this.fundDataService.reload()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (fundIds) => {
          this.notificationService.showSuccess(
            `${fundIds.length} fund IDs loaded successfully`,
            'Fund Data Loaded'
          );
        },
        error: (error: Error) => {
          console.error('[MessageAdd] Failed to reload fund IDs:', error.message);
          this.notificationService.showError(
            'Failed to reload fund IDs. Please try again.',
            'Reload Error'
          );
        }
      });
  }

  // ============================================
  // Computed Properties
  // ============================================

  /**
   * Check if fund fields should be visible
   */
  get showFundFields(): boolean {
    const selectedMessageType = this.formGroup.get('message_type')?.value;

    if (!selectedMessageType) {
      return false;
    }

    const selectedMessageCode = getMessageCodeByValue(selectedMessageType);
    const fundSpecificCode = getMessageCodeByKey(MessageTypeCode.FUND_SPECIFIC_FULL_PAGE);
    return selectedMessageCode === fundSpecificCode;
  }

  /**
   * Check if fund ID dropdown should be disabled
   */
  get isFundIdDisabled(): boolean {
    return this.isFundIdsLoading || this.fundIds.length === 0 || !!this.fundIdsError;
  }

  /**
   * Get fund ID dropdown placeholder text
   */
  get fundIdPlaceholder(): string {
    if (this.isFundIdsLoading) {
      return 'Loading fund IDs...';
    }

    if (this.fundIdsError) {
      return 'Error loading fund IDs';
    }

    if (this.fundIds.length === 0) {
      return 'No fund IDs available';
    }

    return 'Select Fund ID';
  }

  // Text Wrapping Computed Properties
  get hasWrappingErrors(): boolean {
    return this.textValidationErrors.length > 0;
  }

  get wrappingStats(): string {
    if (!this.textWrappingResult) {
      return '';
    }

    const stats = this.textWrappingResult.stats;
    return `${stats.totalLines}/${stats.maxLines} lines | Longest: ${stats.longestLine}/${stats.maxCharsPerLine} chars`;
  }

  get remainingSpaceMessage(): string {
    if (!this.remainingSpace) {
      return '';
    }

    if (!this.remainingSpace.canAddMoreText) {
      return 'Maximum lines reached';
    }

    return `${this.remainingSpace.remainingLines} lines remaining | ${this.remainingSpace.remainingCharsOnCurrentLine} chars on current line`;
  }

  get currentLineCount(): number {
    return this.textWrappingResult?.stats.totalLines || 0;
  }

  get maxLineCount(): number {
    return this.textWrappingResult?.stats.maxLines || 0;
  }

  get isNearLineLimit(): boolean {
    if (!this.textWrappingResult) return false;
    const { totalLines, maxLines } = this.textWrappingResult.stats;
    return totalLines >= maxLines - 1;
  }

  // ============================================
  // Message Type Handling
  // ============================================

  /**
   * Watch for message type changes
   */
  private setupMessageTypeWatcher(): void {
    this.formGroup
      .get('message_type')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe((messageType: string) => {
        this.handleMessageTypeChange(messageType);
      });
  }

  /**
   * Handle message type change
   */
  // eslint-disable-next-line complexity
  private handleMessageTypeChange(messageType: string): void {
    if (!this.showFundFields) {
      this.clearFundFields();
      this.removeFundFieldValidators();
    } else {
      this.addFundFieldValidators();
    }

    const messageTypeCode = getMessageCodeByValue(messageType);
    const maxLines = getMessageLimits(messageTypeCode)?.maxLines || 0;
    const maxChars = getMessageLimits(messageTypeCode)?.maxChars || 0;
    this.maxLinesForCurrentType = maxLines;
    this.maxCharsPerLine = maxChars;
    this.createMessageLineControls(maxLines, maxChars);
  }

  private createMessageLineControls(maxLines: number, maxChars: number): void{
    this.messageLines = [];
    for(let i = 0; i < maxLines; i++){
      if(i === 0){
        const text =  new FormControl('', [(control) => Validators.required(control), (control) => Validators.maxLength(maxChars)(control)]);
        this.messageLines.push(text);
      }
      else{
        const nextTexts = new FormControl('', [(control) => Validators.maxLength(maxChars)(control)]);
        this.messageLines.push(nextTexts);
      }
    }
  }

  hasAnyLineContent(): boolean{
    return this.messageLines.some((line: FormControl) => line.value && String(line.value).length > 0);
  }

  getPreviewMessageLines(): string[]{
    const lines = this.messageLines
      .map((line: FormControl) => line.value as string || '');
    return this.trimTrailingEmptyLines(lines);
  }

  /**
   * Get normalized length for preview display
   * Used in template to show character count with normalized spaces
   */
  getNormalizedLength(value: string | null | undefined): number {
    if (!value) return 0;
    return this.normalizeSpaces(value).length;
  }


  /**
   * Normalize multiple consecutive spaces to single space
   * Matches OLD UI behavior for consistent character counts
   */
  private normalizeSpaces(text: string): string {
    return text;
  }

   /**
   * Handle input event with word-boundary wrapping
   * When text exceeds char limit, moves entire words to next line
   * Uses normalized length for calculations but preserves user input
   */
  // eslint-disable-next-line complexity, max-statements
  onMessageLineInput(index: number, event: Event): void {
    const input = event.target as HTMLInputElement;
    const currentValue = input.value;
    // Normalize for length calculations only (don't change the actual value yet)
    const normalizedValue = this.normalizeSpaces(currentValue);
    const maxLength = this.maxCharsPerLine;

    // No next line available, just truncate based on normalized length
    if (index >= this.messageLines.length - 1) {
      if (normalizedValue.length > maxLength) {
        // Truncate and normalize when saving
        this.messageLines[index].setValue(this.normalizeSpaces(currentValue.substring(0, maxLength)), { emitEvent: false });
      }
      return;
    }

    // Check if we've exceeded or are at the character limit (based on normalized length)
    if (normalizedValue.length >= maxLength) {
      // Find the last space in the normalized text
      const lastSpaceIndex = normalizedValue.lastIndexOf(' ');

      // Word-boundary wrapping: if there's a space, wrap from there
      if (lastSpaceIndex > 0 && normalizedValue.length > maxLength) {
        // Keep text up to (but not including) the last space on current line
        const textToKeep = normalizedValue.substring(0, lastSpaceIndex);
        // Move the rest (after the space) to next line
        const textToMove = normalizedValue.substring(lastSpaceIndex + 1);

        // Update current line with normalized text (wrapping normalizes)
        this.messageLines[index].setValue(textToKeep, { emitEvent: false });

        // Get next line's current value
        const nextLineValue = this.messageLines[index + 1].value || '';

        // Prepend moved text to next line (with space if next line has content)
        const newNextLineValue = nextLineValue
          ? `${textToMove} ${nextLineValue}`
          : textToMove;

        this.messageLines[index + 1].setValue(newNextLineValue, { emitEvent: false });

        // Focus next input
        this.focusNextInput(index);
      } else if (normalizedValue.length > maxLength) {
        // No space found or single long word, just truncate at max length (and normalize)
        this.messageLines[index].setValue(normalizedValue.substring(0, maxLength), {
          emitEvent: false,
        });
      } else {
        // Exactly at max length, just auto-focus to next line
        // This allows user to continue typing on next line
        this.focusNextInput(index);
      }
    }
  }

  /**
   * Handle keydown event for special keys
   * - Backspace at line start: merge with previous line
   * - Regular keys at max length: auto-focus to next line with word wrapping
   */
  // eslint-disable-next-line complexity, max-statements
  onMessageLineKeydown(index: number, event: KeyboardEvent): void {
    const input = event.target as HTMLInputElement;

    // Check if it's a regular character key (not special keys)
    const isRegularKey = event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;

    // Handle regular character at or near max length with word-boundary wrapping
    if (isRegularKey && index < this.messageLines.length - 1) {
      const currentValue = input.value || '';
      const cursorPos = input.selectionStart || 0;
      const selectionEnd = input.selectionEnd || 0;
      const hasSelection = cursorPos !== selectionEnd;

      // Simulate what the value would be after this key is typed
      const valueBeforeCursor = currentValue.substring(0, cursorPos);
      const valueAfterCursor = currentValue.substring(selectionEnd);
      const potentialValue = hasSelection
        ? valueBeforeCursor + event.key + valueAfterCursor
        : valueBeforeCursor + event.key + valueAfterCursor;

      // If typing this character would reach or exceed max length, check for word wrapping
      if (potentialValue.length >= this.maxCharsPerLine) {
        // Find last space for word-boundary wrapping
        const lastSpaceIndex = currentValue.lastIndexOf(' ');

        // Only wrap if there's a space (meaning we're in a multi-word context)
        if (lastSpaceIndex > 0) {
          event.preventDefault();

          // Word-boundary wrap: keep text up to (but not including) last space
          const textToKeep = currentValue.substring(0, lastSpaceIndex);
          const textToMove = currentValue.substring(lastSpaceIndex + 1) + event.key;

          this.messageLines[index].setValue(textToKeep, { emitEvent: false });

          // Get next line's current value
          const nextLineValue = this.messageLines[index + 1].value || '';
          const newNextLineValue = nextLineValue
            ? `${textToMove} ${nextLineValue}`
            : textToMove;

          this.messageLines[index + 1].setValue(newNextLineValue, { emitEvent: false });

          // Focus next input and position cursor after the typed character
          setTimeout(() => {
            const inputElements = this.messageLineInputs.toArray();
            const nextInput = inputElements[index + 1];
            const element = nextInput?.nativeElement as HTMLInputElement | undefined;
            if (element) {
              element.focus();
              element.setSelectionRange(textToMove.length, textToMove.length);
            }
          }, 0);
          return;
        } else if (potentialValue.length > this.maxCharsPerLine) {
          // No space and would exceed limit - prevent the character
          event.preventDefault();
          return;
        }
      }
    }

    // Handle backspace at start of line (merge with previous line)
    if (event.key === 'Backspace' && input.selectionStart === 0 && index > 0) {
      event.preventDefault();

      const currentValue = this.messageLines[index].value || '';
      const previousValue = this.messageLines[index - 1].value || '';

      // Only merge if current line has content
      if (!currentValue) {
        // Just focus previous line if current is empty
        setTimeout(() => {
          const inputElements = this.messageLineInputs.toArray();
          const prevInput = inputElements[index - 1];
          const element = prevInput?.nativeElement as HTMLInputElement | undefined;
          if (element) {
            element.focus();
            element.setSelectionRange(previousValue.length, previousValue.length);
          }
        }, 0);
        return;
      }

      // Merge current line content into previous line
      const mergedValue: string = previousValue ? `${previousValue} ${currentValue}` : currentValue;

      // Check if merged content exceeds max length
      if (mergedValue.length > this.maxCharsPerLine) {
        // Apply word-boundary wrapping
        const textUpToLimit: string = mergedValue.substring(0, this.maxCharsPerLine + 1);
        const lastSpaceIndex: number = textUpToLimit.lastIndexOf(' ');

        if (lastSpaceIndex > 0) {
          const textToKeep: string = mergedValue.substring(0, lastSpaceIndex);
          const textToMove: string = mergedValue.substring(lastSpaceIndex + 1);

          this.messageLines[index - 1].setValue(textToKeep, { emitEvent: false });
          this.messageLines[index].setValue(textToMove, { emitEvent: false });
        } else {
          // Can't split, keep as much as possible in previous line
          const prevLineText: string = mergedValue.substring(0, this.maxCharsPerLine);
          const currentLineText: string = mergedValue.substring(this.maxCharsPerLine);
          this.messageLines[index - 1].setValue(prevLineText, { emitEvent: false });
          this.messageLines[index].setValue(currentLineText, { emitEvent: false });
        }
      } else {
        // Merged content fits in previous line
        this.messageLines[index - 1].setValue(mergedValue, { emitEvent: false });
        this.messageLines[index].setValue('', { emitEvent: false });
      }

      // Focus previous line at the position where merge happened
      setTimeout(() => {
        const inputElements = this.messageLineInputs.toArray();
        const prevInput = inputElements[index - 1];
        const element = prevInput?.nativeElement as HTMLInputElement | undefined;
        if (element) {
          element.focus();
          element.setSelectionRange(previousValue.length, previousValue.length);
        }
      }, 0);

      return;
    }
  }




  /**
   * Handle paste event to distribute content across multiple
  lines
   * Applies word-boundary wrapping for long text
   */
  // eslint-disable-next-line complexity,max-statements
  onMessageLinePaste(index: number, event: ClipboardEvent): void {
    event.preventDefault();
    const pastedText = event.clipboardData?.getData('text') || '';
    if (!pastedText) return;

    const input = event.target as HTMLInputElement;
    const currentValue = input.value || '';
    const cursorPos = input.selectionStart || 0;

    const beforeCursor = currentValue.substring(0, cursorPos);
    const afterCursor = currentValue.substring(input.selectionEnd || cursorPos);

    const pastedLines = pastedText.split(/\r?\n/);

    let currentIndex = index;
    for (let i = 0; i < pastedLines.length && currentIndex < this.messageLines.length; i++) {
      let contentForLine = pastedLines[i];
      if (i === 0) {
        contentForLine = beforeCursor + contentForLine;
      }
      if (i === pastedLines.length - 1) {
        contentForLine += afterCursor;
      }
      this.applyTextWithWrapping(currentIndex, contentForLine);
      currentIndex++;
    }
  }

  /**
   * Handle multi-line paste (when text contains newlines)
   */
  /**
   * Apply text with word-boundary wrapping across multiple lines
   * Preserves spacing as typed/pasted (normalization happens only for counting)
   */
  private applyTextWithWrapping(startIndex: number, text: string): void {
    // Preserve original spacing (normalize only for character counting, not display)
    let remainingText = text;
    let currentIndex = startIndex;

    while (remainingText.length > 0 && currentIndex < this.messageLines.length) {
      if (remainingText.length <= this.maxCharsPerLine) {
        // Text fits in current line
        this.messageLines[currentIndex].setValue(remainingText, { emitEvent: false });
        break;
      }

      // Text exceeds limit, apply word-boundary wrapping
      const textUpToLimit = remainingText.substring(0, this.maxCharsPerLine + 1);
      const lastSpaceIndex = textUpToLimit.lastIndexOf(' ');

      if (lastSpaceIndex > 0) {
        // Found a space, split at word boundary
        const textToKeep = remainingText.substring(0, lastSpaceIndex);
        this.messageLines[currentIndex].setValue(textToKeep, { emitEvent: false });
        remainingText = remainingText.substring(lastSpaceIndex + 1);
      } else {
        // No space found, truncate at max length
        this.messageLines[currentIndex].setValue(
          remainingText.substring(0, this.maxCharsPerLine),
          { emitEvent: false }
        );
        remainingText = remainingText.substring(this.maxCharsPerLine);
      }

      currentIndex++;
    }
  }

  private focusNextInput(currentIndex: number): void {
    setTimeout(() => {
      const inputElements = this.messageLineInputs?.toArray();
      const nextInput = inputElements?.[currentIndex + 1];
      const element = nextInput?.nativeElement as HTMLInputElement | undefined;
      element?.focus();
    }, 0);
  }

  /**
   * Get character count warning message for a line
   */

  getCharCountMessage(index: number): string{
    const lineValue = this.messageLines[index].value || '';
    // Normalize spaces for character count to match OLD UI behavior
    const normalizedValue = this.normalizeSpaces(lineValue);
    const currentLength = normalizedValue.length;
    const maxLength = this.maxCharsPerLine;

    return `${currentLength}/${maxLength} chars`;
  }
  /**
   * Get remaining characters for a line
   */
  getRemainingChars(index: number): number{
    const lineValue = this.messageLines[index].value || '';
    // Normalize spaces for remaining chars calculation to match OLD UI behavior
    const normalizedValue = this.normalizeSpaces(lineValue);
    return this.maxCharsPerLine - normalizedValue.length;
  }

  /**
   * Check if line is near character limit (15 or less
  remaining)
   */
  isLineNearLimit(index: number): boolean {
    const remaining = this.getRemainingChars(index);
    return remaining <= 15 && remaining >= 0;
  }

  /**
   * Check if line has reached character limit
   */
  isLineLimitReached(index: number): boolean{
    const lineValue = this.messageLines[index].value || '';
    return lineValue.length >= this.maxCharsPerLine;
  }


  /**
   * Get alert class for character count badge
  */

  getCharCountTextClass(index: number): string {
    if (this.isLineLimitReached(index)) {
      return 'text-danger'; // Red when limit reached
    }
    if (this.isLineNearLimit(index)) {
      return 'text-warning'; // Orange when near limit (15 or less)
    }
    return 'text-muted'; // Blue for normal info
  }

  /**
   * Clear fund field values
   */
  private clearFundFields(): void {
    this.formGroup.patchValue(
      {
        fund_id: '',
        fund_code: '',
      },
      { emitEvent: false },
    );
  }

  /**
   * Add required validators to fund fields
   */
  private addFundFieldValidators(): void {
    const fundIdControl = this.formGroup.get('fund_id');
    const fundCodeControl = this.formGroup.get('fund_code');

    fundIdControl?.setValidators([(control) => Validators.required(control)]);
    fundCodeControl?.setValidators([(control) => Validators.required(control)]);

    fundIdControl?.updateValueAndValidity();
    fundCodeControl?.updateValueAndValidity();
  }

  /**
   * Remove validators from fund fields
   */
  private removeFundFieldValidators(): void {
    const fundIdControl = this.formGroup.get('fund_id');
    const fundCodeControl = this.formGroup.get('fund_code');

    fundIdControl?.clearValidators();
    fundCodeControl?.clearValidators();

    fundIdControl?.updateValueAndValidity();
    fundCodeControl?.updateValueAndValidity();
  }

  /**
   * Clear text wrapping state
   */
  private clearTextWrappingState(): void {
    this.textWrappingResult = null;
    this.wrappedMessageLines = [];
    this.textValidationErrors = [];
    this.remainingSpace = null;
  }


  // ============================================
  // Form Validation
  // ============================================

  /**
   * Check if form has unsaved changes (for guard)
   */
  hasUnsavedChanges(): boolean {
    return this.hasFormData() && !this.isWaiting;
  }

  /**
   * Check if form has any data
   */
  hasFormData(): boolean {
    const values = this.formGroup.value;
    const hasFormValues = Object.values(values).some((value) =>
    this.hasValue(value));
    const hasMessageLineData =
    this.messageLines.some((line: FormControl) => line.value &&
    String(line.value).length > 0);
    return hasFormValues || hasMessageLineData;
  }

  /**
   * Check if a form value is considered "filled"
   */
  private hasValue(value: unknown): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    return value !== null && value !== undefined && value !== '';
  }

  // ============================================
  // Form Submission
  // ============================================

  /**
   * Handle form submission with text validation
   */
  // eslint-disable-next-line complexity, max-statements
  submitForm(event: Event): void {
    event.preventDefault();

    // Validate Line 1 is not empty (required field)
    if (this.messageLines.length > 0) {
      const line1Value = this.messageLines[0].value || '';
      if (line1Value.length === 0) {
        this.showErrors = true;
        this.messageLines[0].markAsTouched();
        this.messageLines[0].setErrors({ required: true });
        this.handleInvalidForm(event);
        return;
      }
    }

    // Validate text wrapping first
    if (!this.validateMessageTextBeforeSubmit()) {
      this.handleInvalidForm(event);
      return;
    }

    if (this.formGroup.invalid) {
      this.handleInvalidForm(event);
      return;
    }

    this.submitConfirmationModal.openModalDialog();
  }

  /**
   * Validate message text before submission
   */
  // eslint-disable-next-line complexity
  private validateMessageTextBeforeSubmit(): boolean {
    const messageText = this.formGroup.get('text')?.value || '';
    const messageType = this.formGroup.get('message_type')?.value;

    if (!messageType) {
      return true; // Skip validation if no message type selected
    }

    const messageTypeCode = getMessageCodeByValue(messageType);
    const isValid = validateTextForMessageType(messageText, messageTypeCode);

    if (!isValid) {
      const errors = getTextValidationErrors(messageText, messageTypeCode);
      this.textValidationErrors = errors;
      this.showErrors = true;

      this.notificationService.showError(errors.join(', '), 'Message text validation failed');
    }

    return isValid;
  }

  /**
   * Handle invalid form state
   */
  private handleInvalidForm(event: Event): void {
    this.showErrors = true;
    this.focusFirstInvalidInput(event);
    this.scrollToInvalidField();
  }

  /**
   * Focus first invalid input
   */
  private focusFirstInvalidInput(event: Event): void {
    const target = event.target as HTMLElement;
    const firstInvalid: HTMLElement | null = target?.querySelector('.ng-invalid');
    firstInvalid?.focus();
  }

  /**
   * Scroll invalid field into view
   */
  private scrollToInvalidField(): void {
    setTimeout(() => {
      this.document.activeElement?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    });
  }

  /**
   * User confirms submission
   */
  confirmSubmit(): void {
    this.prepareForSubmission();

    const payload = this.buildAddPayload();
    this.httpClient
      .addMessage(payload)
      .pipe(
        takeUntil(this.destroy$),
        switchMap((response: AddUpdateDeleteResponseModel) => {
          this.handleAddSuccess(response);
          return this.httpClient.getMessageDetails();
        }),
      )
      .subscribe({
        next: (messages: StatementMessagingMessageModel) =>
          this.handleFetchMessagesSuccess(messages),
        error: (error: Error) => this.handleOperationError(error),
      });
  }

  /**
   * Prepare component state for submission
   */
  private prepareForSubmission(): void {
    this.isWaiting = true;
    this.bypassSubmit = true;
    this.submitConfirmationModal.closeDialogModal();
    this.messageDataService.setLoading(true);
  }

  /**
   * User cancels submission
   */
  cancelSubmit(): void {
    this.submitConfirmationModal.closeDialogModal();
  }

  // ============================================
  // Payload Builder
  // ============================================

  /**
   * Build add message payload with wrapped text
   */
  private buildAddPayload(): AddUpdateDeleteMessageModel {
    const formValue = this.formGroup.value;
    const messageId = formValue.message_number as string;
    const headerValue = (formValue.header as string) || '';

    const messageTextList = this.buildMessageTextList(headerValue);

    const payload = this.createBasePayload(messageId, formValue, messageTextList);

    this.addFundFieldsIfApplicable(payload, formValue);

    return payload;
  }

  private buildMessageTextList(header: string): string[]{
    const lines: string[] = [];
    for(const lineControl of this.messageLines){
      lines.push(lineControl.value || '');
    }
    return [header, ...this.trimTrailingEmptyLines(lines)];
  }

  private trimTrailingEmptyLines(lines: string[]): string[] {
    let end = lines.length;
    while (end > 0 && lines[end - 1].trim() === '') {
      end--;
    }
    return lines.slice(0, end);
  }

  /**
   * Create base payload structure
   */
  private createBasePayload(
    messageId: string,
    formValue: Record<string, unknown>,
    messageTextList: string[],
  ): AddUpdateDeleteMessageModel {
    return {
      actionCode: this.ACTION_CODE_ADD,
      messageId: this.padString(messageId, this.PADDING_MESSAGE_ID),
      messageTypeCode: getMessageCodeByValue(formValue.message_type as string),
      messageTextList,
      permanentMessageFlag: (formValue.isPermanent as boolean) || false,
      displayFundMessageCode: null,
      portId: null,
      verifyUserId: this.oktaService.getCrewId(),
      messageIdWithoutPadding: messageId,
    };
  }

  /**
   * Add fund fields to payload if applicable
   */
  private addFundFieldsIfApplicable(
    payload: AddUpdateDeleteMessageModel,
    formValue: Record<string, unknown>,
  ): void {
    if (this.showFundFields) {
      payload.displayFundMessageCode = getFundCodeByLabel(formValue.fund_code as string);
      payload.portId = formValue.fund_id as string;
    }
  }

  /**
   * Pad string with leading zeros
   */
  private padString(value: number | string, length: number): string {
    return value.toString().padStart(length, '0');
  }

  // ============================================
  // Response Handlers
  // ============================================

  /**
   * Handle successful message add
   */
  private handleAddSuccess(response: AddUpdateDeleteResponseModel): void {
    this.notificationService.showSuccess(
      `Message ${response.confirmationNumber} added successfully.`,
      'You successfully added a message',
    );
  }

  /**
   * Handle successful fetch of updated messages
   */
  private handleFetchMessagesSuccess(messages: StatementMessagingMessageModel): void {
    this.messageDataService.setMessageData(messages);
    this.cleanupAndNavigate();
  }

  /**
   * Handle operation error
   */
  private handleOperationError(error: Error): void {
    this.notificationService.showError(
      'Failed to add message.',
      'Please check your input and try again.',
    );
    this.messageDataService.setError(error.message);
    this.cleanupAndNavigate();
  }

  // ============================================
  // Form Management
  // ============================================

  /**
   * Reset form to initial state
   */
  resetForm(): void {
    this.formGroup.reset({
      message_number: '',
      message_type: '',
      fund_id: '',
      fund_code: '',
      isPermanent: false,
      text: '',
      header: '',
    });
    this.messageLines = [];
    this.isWaiting = false;
    this.showErrors = false;
    this.clearTextWrappingState();
  }

  // ============================================
  // Preview Actions
  // ============================================

  /**
   * Open preview modal
   */
  onPreviewViewMessage(): void {
    this.messagePreviewModal.openModal();
  }

  /**
   * Close preview modal
   */
  onClosePreviewMessage(): void {
    this.messagePreviewModal.closeModal();
  }

  // ============================================
  // Navigation & Cleanup
  // ============================================

  /**
   * Cleanup and navigate to home
   */
  private cleanupAndNavigate(): void {
    this.isWaiting = false;
    this.clearSelections();
    this.navigateToHome();
  }

  /**
   * Clear all selections
   */
  private clearSelections(): void {
    this.selectedMessageService.clearSelection();
  }

  /**
   * Navigate to home page
   */
  private navigateToHome(): void {
    this.router.navigate(['/']).then(
      () => {
        // Navigation successful
      },
      () => {
        // Navigation failed
      },
    );
  }

  /**
   * Handle cancel button click
   */
  cancelButtonHandler(): void {
    this.navigateToHome();
  }

  // ============================================
  // Unsaved Changes Modal Actions
  // ============================================

  /**
   * User confirms they want to leave
   */
  leavePage(): void {
    this.confirmationModal.closeDialogModal();
    this.bypassSubmit = true;
    this.clearSelections();
    this.navigateToHome();
  }

  /**
   * User wants to stay on page
   */
  stayOnPage(): void {
    this.confirmationModal.closeDialogModal();
  }
}
